import type {
    AuthenticationBootstrapPhase,
    AuthenticationState
} from '$lib/services/authentication/authentication.svelte.js'

export type AuthenticationRoutingState = {
    bootstrapPhase: AuthenticationBootstrapPhase
    state: AuthenticationState
}

export type LoginPath = '/login' | `/login?${string}`
export type LoginHelpPath = '/login/help' | `/login/help?${string}`
export type PasswordResetPath = '/login/reset' | `/login/reset?${string}`
export type NewPasswordPath = '/login/reset/password' | `/login/reset/password?${string}`

export function getDefaultAuthenticatedPath(): string {
    return '/home'
}

export function getDefaultUnauthenticatedPath(): string {
    return '/login'
}

export function getRootRedirectPath(
    authentication: AuthenticationRoutingState
): string | undefined {
    if (authentication.bootstrapPhase !== 'ready') {
        return undefined
    }

    if (authentication.state.phase === 'authenticated') {
        return getDefaultAuthenticatedPath()
    }

    return getDefaultUnauthenticatedPath()
}

export function getUnauthenticatedRedirectPath(currentUrl: URL): string {
    return buildLoginPath(getCurrentPath(currentUrl))
}

export function getLoginPathForCurrentAttempt(currentUrl: URL): LoginPath {
    const next = getSafePostLoginPath(currentUrl)

    if (!next) {
        return '/login'
    }

    const searchParams = new URLSearchParams({
        next
    })

    return `/login?${searchParams.toString()}`
}

export function getLoginHelpPathForCurrentAttempt(currentUrl: URL): LoginHelpPath {
    const next = getSafePostLoginPath(currentUrl)

    if (!next) {
        return '/login/help'
    }

    const searchParams = new URLSearchParams({
        next
    })

    return `/login/help?${searchParams.toString()}`
}

export function getPasswordResetPathForCurrentAttempt(currentUrl: URL): PasswordResetPath {
    const next = getSafePostLoginPath(currentUrl)

    if (!next) {
        return '/login/reset'
    }

    const searchParams = new URLSearchParams({
        next
    })

    return `/login/reset?${searchParams.toString()}`
}

export function getNewPasswordPathForCurrentAttempt(currentUrl: URL): NewPasswordPath {
    const next = getSafePostLoginPath(currentUrl)

    if (!next) {
        return '/login/reset/password'
    }

    const searchParams = new URLSearchParams({
        next
    })

    return `/login/reset/password?${searchParams.toString()}`
}

export function getExplicitLogoutRedirectPath(): string {
    return getDefaultUnauthenticatedPath()
}

export function getAccountMutationCompletionRedirectPath(
    authenticationState: AuthenticationState
): LoginPath | undefined {
    if (authenticationState.phase !== 'logged-out') {
        return undefined
    }

    if (
        authenticationState.reason !== 'account-deletion-completed' &&
        authenticationState.reason !== 'account-deletion-manual-removal-completed' &&
        authenticationState.reason !== 'email-change-completed'
    ) {
        return undefined
    }

    return '/login'
}

export function getAuthenticatedRedirectPath(currentUrl: URL): string {
    return getPostLoginPath(currentUrl, getDefaultAuthenticatedPath())
}

export function shouldInvalidateRouteAuthorization(
    previousState: AuthenticationState,
    currentState: AuthenticationState
): boolean {
    return isAuthenticatedState(previousState) !== isAuthenticatedState(currentState)
}

export function shouldReevaluateRouteAuthorization(
    previousState: AuthenticationState,
    currentState: AuthenticationState
): boolean {
    return (
        shouldInvalidateRouteAuthorization(previousState, currentState) ||
        isAccountDeletionCompletionTransition(previousState, currentState)
    )
}

export function invalidateRouteAuthorizationOnAuthenticationChange(
    previousState: AuthenticationState,
    currentState: AuthenticationState,
    invalidate: () => void | Promise<void>
): boolean {
    if (!shouldReevaluateRouteAuthorization(previousState, currentState)) {
        return false
    }

    void invalidate()
    return true
}

export function getPostLoginPath(currentUrl: URL, fallbackPath: string): string {
    const next = getSafePostLoginPath(currentUrl)

    if (!next) {
        return fallbackPath
    }

    return next
}

function buildLoginPath(next: string): string {
    const searchParams = new URLSearchParams({
        next
    })

    return `${getDefaultUnauthenticatedPath()}?${searchParams.toString()}`
}

function getCurrentPath(url: URL): string {
    return `${url.pathname}${url.search}`
}

function isUnauthenticatedPath(path: string): boolean {
    const unauthenticatedPath = getDefaultUnauthenticatedPath()

    return (
        path === unauthenticatedPath ||
        path.startsWith(`${unauthenticatedPath}?`) ||
        path.startsWith(`${unauthenticatedPath}#`) ||
        path.startsWith(`${unauthenticatedPath}/`)
    )
}

function getSafePostLoginPath(currentUrl: URL): string | undefined {
    const next = getSafeSameOriginPath(currentUrl, 'next')

    if (!next || isUnauthenticatedPath(next.decodedPathname)) {
        return undefined
    }

    return `${next.url.pathname}${next.url.search}${next.url.hash}`
}

function getSafeSameOriginPath(
    currentUrl: URL,
    parameterName: string
): { decodedPathname: string; url: URL } | undefined {
    const path = currentUrl.searchParams.get(parameterName)

    if (
        !path?.startsWith('/') ||
        path.startsWith('//') ||
        path.includes('\\') ||
        /%(?:25)*5c/i.test(path)
    ) {
        return undefined
    }

    let url: URL

    try {
        url = new URL(path, currentUrl.origin)
    } catch {
        return undefined
    }

    let decodedPathname: string

    try {
        decodedPathname = decodeURIComponent(url.pathname)
    } catch {
        return undefined
    }

    if (
        url.origin !== currentUrl.origin ||
        !decodedPathname.startsWith('/') ||
        decodedPathname.startsWith('//') ||
        decodedPathname.includes('\\')
    ) {
        return undefined
    }

    return { decodedPathname, url }
}

function isAuthenticatedState(state: AuthenticationState): boolean {
    return state.phase === 'authenticated' || state.phase === 'logging-out'
}

function isAccountDeletionCompletionTransition(
    previousState: AuthenticationState,
    currentState: AuthenticationState
): boolean {
    if (
        previousState.phase !== 'logged-out' ||
        previousState.reason !== 'account-deletion-manual-removal-required' ||
        currentState.phase !== 'logged-out'
    ) {
        return false
    }

    return (
        currentState.reason === 'account-deletion-completed' ||
        currentState.reason === 'account-deletion-manual-removal-completed'
    )
}
